import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCalendarWeek,
  faPlus,
  faEdit,
  faBan,
  faSpinner,
  faBox,
  faTruck,
  faCircleCheck,
  faTriangleExclamation,
  faRotateRight,
  faChevronDown,
  faChevronUp,
  faPlay,
} from '@fortawesome/free-solid-svg-icons';
import toast from 'react-hot-toast';
import { orderService } from '../../services/order';

const formatRunTimestamp = (isoString) =>
  isoString ? new Date(isoString).toLocaleString('pt-PT') : '—';

const STATUS_LABELS = {
  pending:   { label: 'Pendente',  classes: 'bg-yellow-100 text-yellow-700' },
  packed:    { label: 'Embalada',  classes: 'bg-blue-100 text-blue-700' },
  delivered: { label: 'Entregue',  classes: 'bg-green-100 text-green-700' },
  cancelled: { label: 'Cancelada', classes: 'bg-red-100 text-red-600' },
  overdue:   { label: 'Atrasada',  classes: 'bg-orange-100 text-orange-700' },
  divided:   { label: 'Dividida',  classes: 'bg-purple-100 text-purple-700' },
};

// Encomenda semanal "mãe" — is_weekly=true e weekly_origin_id=null. É a base
// que o weeklyOrdersScheduler duplica automaticamente todas as segundas-feiras.
const WeeklyOrdersSection = () => {
  const navigate = useNavigate();
  const [chains, setChains]   = useState([]); // [{ root, orders }]
  const [loading, setLoading] = useState(true);
  const [cancelTarget, setCancelTarget] = useState(null); // { id, order_number }
  const [cancelling, setCancelling]     = useState(false);
  const [reactivatingId, setReactivatingId] = useState(null);
  const [showInactive, setShowInactive]     = useState(false);

  const [logs, setLogs]             = useState([]);
  const [logsLoading, setLogsLoading] = useState(true);
  const [retryingId, setRetryingId]   = useState(null);
  const [showHistory, setShowHistory] = useState(false);

  const loadOrders = useCallback(async () => {
    setLoading(true);
    try {
      const data = await orderService.getWeeklyChains();
      setChains(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error('Erro ao carregar encomendas semanais: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const activeChains   = chains.filter(c => c.root.is_weekly);
  const inactiveChains = chains.filter(c => !c.root.is_weekly);

  const handleReactivate = async (rootId) => {
    setReactivatingId(rootId);
    try {
      await orderService.reactivateWeekly(rootId);
      toast.success('Recorrência reativada — volta a gerar encomendas às segundas-feiras');
      loadOrders();
    } catch (err) {
      toast.error(err.message || 'Erro ao reativar recorrência');
    } finally {
      setReactivatingId(null);
    }
  };

  const loadLogs = useCallback(async () => {
    setLogsLoading(true);
    try {
      const data = await orderService.getWeeklySchedulerLogs(20);
      setLogs(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error('Erro ao carregar histórico de execuções: ' + err.message);
    } finally {
      setLogsLoading(false);
    }
  }, []);

  useEffect(() => { loadOrders(); loadLogs(); }, [loadOrders, loadLogs]);

  const handleRetry = async (log, item) => {
    setRetryingId(item.root_order_id);
    try {
      const result = await orderService.retryWeekly(item.root_order_id);
      if (result.status === 'created') {
        toast.success(result.message);
      } else {
        toast(result.message, { icon: 'ℹ️' });
      }
      // Remove da lista de falhas mostrada — já foi resolvida manualmente.
      setLogs(prev => prev.map(l => (
        l.id === log.id
          ? { ...l, errors: l.errors.filter(e => e.root_order_id !== item.root_order_id) }
          : l
      )));
    } catch (err) {
      toast.error(err.message || 'Erro ao recriar encomenda semanal');
    } finally {
      setRetryingId(null);
    }
  };

  const handleCancel = async () => {
    if (!cancelTarget) return;
    setCancelling(true);
    try {
      await orderService.cancelWeekly(cancelTarget.id);
      toast.success(`Recorrência de ${cancelTarget.order_number} cancelada`);
      setCancelTarget(null);
      loadOrders();
    } catch (err) {
      toast.error(err.message || 'Erro ao cancelar recorrência');
    } finally {
      setCancelling(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-xs text-neutral-500 max-w-xl">
          Estas são as encomendas semanais "mãe" — a base que gera automaticamente
          uma nova encomenda e expedição todas as semanas. Edite os itens aqui para
          que fiquem corretos nas próximas duplicações, ou cancele a recorrência
          para parar de gerar novas e criar outra encomenda semanal no lugar.
        </p>
        <button
          onClick={() => navigate('/create-order')}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg font-medium transition-colors shrink-0"
        >
          <FontAwesomeIcon icon={faPlus} />
          Nova encomenda semanal
        </button>
      </div>

      {/* ---- LOG DA ÚLTIMA EXECUÇÃO DO SCHEDULER ---- */}
      {logsLoading ? (
        <div className="flex items-center gap-2 text-xs text-neutral-400 mb-4">
          <FontAwesomeIcon icon={faSpinner} spin />
          A carregar histórico de execuções...
        </div>
      ) : logs.length === 0 ? (
        <div className="bg-neutral-50 border border-neutral-200 rounded-lg p-3 mb-4 text-xs text-neutral-500">
          Ainda não há registo de nenhuma execução do processamento semanal (corre às segundas-feiras).
        </div>
      ) : (
        <div className="mb-4 space-y-2">
          {(() => {
            const latest = logs[0];
            const hasErrors = (latest.errors || []).length > 0;
            return (
              <div className={`border rounded-lg p-3 ${hasErrors ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-2 text-sm font-medium">
                    <FontAwesomeIcon
                      icon={hasErrors ? faTriangleExclamation : faCircleCheck}
                      className={hasErrors ? 'text-red-500' : 'text-green-600'}
                    />
                    <span className={hasErrors ? 'text-red-700' : 'text-green-700'}>
                      Última execução: {formatRunTimestamp(latest.ran_at)}
                    </span>
                  </div>
                  <div className="text-xs text-neutral-600">
                    {(latest.orders_created || []).length} criada{(latest.orders_created || []).length !== 1 ? 's' : ''} ·{' '}
                    {(latest.orders_skipped || []).length} ignorada{(latest.orders_skipped || []).length !== 1 ? 's' : ''} ·{' '}
                    {(latest.errors || []).length} com erro
                  </div>
                </div>

                {hasErrors && (
                  <div className="mt-2 space-y-1.5">
                    {latest.errors.map((item, i) => (
                      <div key={i} className="flex items-center justify-between gap-2 bg-white border border-red-100 rounded px-2.5 py-1.5">
                        <div className="min-w-0">
                          <span className="text-xs font-medium text-neutral-800">{item.client_name || item.root_order_number}</span>
                          <span className="text-xs text-neutral-400"> ({item.root_order_number})</span>
                          <p className="text-xs text-red-600 truncate">{item.message}</p>
                        </div>
                        <button
                          onClick={() => handleRetry(latest, item)}
                          disabled={retryingId === item.root_order_id || !item.root_order_id}
                          title={!item.root_order_id ? 'Execução antiga sem ID da encomenda raiz' : 'Tentar criar novamente'}
                          className="flex items-center gap-1 px-2 py-1 text-xs bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white rounded shrink-0"
                        >
                          <FontAwesomeIcon icon={faRotateRight} spin={retryingId === item.root_order_id} />
                          Tentar novamente
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {(latest.orders_created || []).length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {latest.orders_created.map((item, i) => (
                      <span key={i} className="text-xs bg-white border border-green-200 text-green-700 rounded px-2 py-0.5">
                        {item.client_name}: {item.order_number} (entrega {new Date(item.delivery_date + 'T00:00:00').toLocaleDateString('pt-PT')})
                      </span>
                    ))}
                  </div>
                )}
              </div>
            );
          })()}

          {logs.length > 1 && (
            <button
              onClick={() => setShowHistory(prev => !prev)}
              className="text-xs text-neutral-500 hover:text-neutral-700 flex items-center gap-1"
            >
              <FontAwesomeIcon icon={showHistory ? faChevronUp : faChevronDown} />
              {showHistory ? 'Ocultar' : 'Ver'} histórico de execuções anteriores ({logs.length - 1})
            </button>
          )}

          {showHistory && (
            <div className="space-y-1">
              {logs.slice(1).map(log => {
                const errCount = (log.errors || []).length;
                return (
                  <div key={log.id} className="flex items-center justify-between text-xs text-neutral-500 px-2 py-1 border-b border-neutral-100">
                    <span>{formatRunTimestamp(log.ran_at)}</span>
                    <span>
                      {(log.orders_created || []).length} criada{(log.orders_created || []).length !== 1 ? 's' : ''} ·{' '}
                      {(log.orders_skipped || []).length} ignorada{(log.orders_skipped || []).length !== 1 ? 's' : ''} ·{' '}
                      <span className={errCount > 0 ? 'text-red-500 font-medium' : ''}>{errCount} com erro</span>
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <FontAwesomeIcon icon={faSpinner} spin className="text-blue-500 text-2xl mr-3" />
          <span className="text-neutral-500">A carregar...</span>
        </div>
      ) : chains.length === 0 ? (
        <div className="bg-white border border-neutral-200 rounded-lg py-16 text-center">
          <FontAwesomeIcon icon={faCalendarWeek} className="text-neutral-300 text-4xl mb-3" />
          <p className="text-neutral-500 text-sm">Nenhuma encomenda semanal encontrada</p>
          <button onClick={() => navigate('/create-order')} className="mt-4 text-blue-600 text-sm hover:underline">
            Criar a primeira encomenda semanal
          </button>
        </div>
      ) : (
        <>
          {activeChains.length === 0 ? (
            <div className="bg-white border border-neutral-200 rounded-lg py-10 text-center mb-4">
              <p className="text-neutral-500 text-sm">Nenhuma recorrência ativa neste momento</p>
              <button onClick={() => navigate('/create-order')} className="mt-2 text-blue-600 text-sm hover:underline">
                Criar uma encomenda semanal
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {activeChains.map(({ root: order }) => {
                const status = STATUS_LABELS[order.delivery_status] || { label: order.delivery_status, classes: 'bg-neutral-100 text-neutral-600' };
                return (
                  <div key={order.id} className="bg-white border border-neutral-200 rounded-lg p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-neutral-900 text-sm">{order.order_number}</span>
                        <span className="text-sm text-neutral-700">{order.client_name}</span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${status.classes}`}>
                          {status.label}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-4 mt-1.5 text-xs text-neutral-500">
                        <span className="flex items-center gap-1">
                          <FontAwesomeIcon icon={faTruck} />
                          Última entrega: {order.delivery_date ? new Date(order.delivery_date + 'T00:00:00').toLocaleDateString('pt-PT') : '—'}
                        </span>
                        <span className="flex items-center gap-1">
                          <FontAwesomeIcon icon={faBox} />
                          {(order.order_items || []).length} produto{(order.order_items || []).length !== 1 ? 's' : ''}
                        </span>
                      </div>
                      {(order.order_items || []).length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1">
                          {order.order_items.slice(0, 4).map((item, i) => (
                            <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 bg-neutral-100 text-neutral-700 text-xs rounded">
                              {item.products?.name || item.product_id.slice(0, 8) + '…'} × {item.ordered_quantity}
                            </span>
                          ))}
                          {order.order_items.length > 4 && (
                            <span className="text-xs text-neutral-400">+{order.order_items.length - 4} mais</span>
                          )}
                        </div>
                      )}
                    </div>

                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button
                        onClick={() => navigate(`/order-details/${order.id}`)}
                        title="Editar itens da encomenda"
                        className="flex items-center gap-1 px-2.5 py-1.5 border border-neutral-200 text-neutral-600 text-xs rounded hover:bg-blue-50 hover:text-blue-600 transition-colors"
                      >
                        <FontAwesomeIcon icon={faEdit} />
                        Editar
                      </button>
                      <button
                        onClick={() => setCancelTarget({ id: order.id, order_number: order.order_number })}
                        title="Cancelar recorrência semanal"
                        className="flex items-center gap-1 px-2.5 py-1.5 border border-neutral-200 text-neutral-600 text-xs rounded hover:bg-red-50 hover:text-red-600 transition-colors"
                      >
                        <FontAwesomeIcon icon={faBan} />
                        Cancelar recorrência
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* ---- RECORRÊNCIAS INATIVAS / CANCELADAS ---- */}
          {inactiveChains.length > 0 && (
            <div className="mt-6">
              <button
                onClick={() => setShowInactive(prev => !prev)}
                className="text-xs text-neutral-500 hover:text-neutral-700 flex items-center gap-1 mb-2"
              >
                <FontAwesomeIcon icon={showInactive ? faChevronUp : faChevronDown} />
                {showInactive ? 'Ocultar' : 'Ver'} recorrências inativas/canceladas ({inactiveChains.length})
              </button>

              {showInactive && (
                <div className="space-y-2">
                  {inactiveChains.map(({ root: order, orders: childOrders }) => (
                    <div key={order.id} className="bg-neutral-50 border border-neutral-200 rounded-lg p-3 flex flex-col sm:flex-row sm:items-center gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-neutral-700 text-sm">{order.order_number}</span>
                          <span className="text-sm text-neutral-600">{order.client_name}</span>
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-neutral-200 text-neutral-600">
                            Recorrência cancelada
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-4 mt-1 text-xs text-neutral-400">
                          <span>Mãe original: {order.delivery_date ? new Date(order.delivery_date + 'T00:00:00').toLocaleDateString('pt-PT') : '—'}</span>
                          <span>{childOrders.length} encomenda{childOrders.length !== 1 ? 's' : ''} geradas nesta cadeia</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <button
                          onClick={() => navigate(`/order-details/${order.id}`)}
                          title="Ver encomenda"
                          className="flex items-center gap-1 px-2.5 py-1.5 border border-neutral-200 text-neutral-500 text-xs rounded hover:bg-white transition-colors"
                        >
                          <FontAwesomeIcon icon={faEdit} />
                          Ver
                        </button>
                        <button
                          onClick={() => handleReactivate(order.id)}
                          disabled={reactivatingId === order.id}
                          title="Reativar recorrência semanal"
                          className="flex items-center gap-1 px-2.5 py-1.5 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white text-xs rounded transition-colors"
                        >
                          <FontAwesomeIcon icon={reactivatingId === order.id ? faSpinner : faPlay} spin={reactivatingId === order.id} />
                          Reativar
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* ---- CONFIRMAÇÃO DE CANCELAMENTO ---- */}
      {cancelTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm p-5">
            <h3 className="font-semibold text-neutral-900 mb-2">Cancelar recorrência</h3>
            <p className="text-sm text-neutral-600 mb-4">
              A encomenda <strong>{cancelTarget.order_number}</strong> deixa de gerar novas
              encomendas/expedições semanais. As duplicatas já criadas não são afetadas.
              Podes criar uma nova encomenda semanal a qualquer momento.
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setCancelTarget(null)}
                className="px-3 py-1.5 text-sm text-neutral-600 hover:bg-neutral-100 rounded"
              >
                Voltar
              </button>
              <button
                onClick={handleCancel}
                disabled={cancelling}
                className="px-3 py-1.5 text-sm bg-red-600 hover:bg-red-700 disabled:opacity-60 text-white rounded flex items-center gap-2"
              >
                {cancelling && <FontAwesomeIcon icon={faSpinner} spin />}
                Confirmar cancelamento
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WeeklyOrdersSection;
